
import { useState } from 'react';

export default function Home() {
  const [donationAmount, setDonationAmount] = useState('50');
  const [donationType, setDonationType] = useState('one-time');
  const [donationForm, setDonationForm] = useState({
    fullName: '',
    email: '',
  });
  const [volunteerForm, setVolunteerForm] = useState({
    name: '',
    email: '',
    phone: '',
    interests: [],
    availability: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDonationSubmitting, setIsDonationSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');
  const [donationSubmitStatus, setDonationSubmitStatus] = useState('');

  const handleVolunteerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!volunteerForm.name.trim() || !volunteerForm.email.trim()) {
      setSubmitStatus('Please fill in all required fields.');
      return;
    }

    // Validate message length
    if (volunteerForm.message.length > 500) {
      setSubmitStatus('Message cannot exceed 500 characters.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Prepare form data in application/x-www-form-urlencoded format
      const formData = new URLSearchParams();
      formData.append('name', volunteerForm.name);
      formData.append('email', volunteerForm.email);
      formData.append('phone', volunteerForm.phone);
      formData.append('interests', volunteerForm.interests.join(', '));
      formData.append('message', volunteerForm.message);

      const response = await fetch(
        'https://readdy.ai/api/form/d3rvs1s1cr8rcdqfdjv0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setSubmitStatus(
          'Thank you for your interest in volunteering! We will contact you soon.'
        );
        // Reset form
        setVolunteerForm({
          name: '',
          email: '',
          phone: '',
          interests: [],
          availability: '',
          message: '',
        });
      } else {
        setSubmitStatus(
          'There was an error submitting your application. Please try again.'
        );
      }
    } catch (error) {
      setSubmitStatus(
        'There was an error submitting your application. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDonationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!donationForm.fullName.trim() || !donationForm.email.trim()) {
      setDonationSubmitStatus('Please fill in all required fields.');
      return;
    }

    // Validate donation amount
    if (!donationAmount || parseFloat(donationAmount) < 1) {
      setDonationSubmitStatus('Please enter a valid donation amount.');
      return;
    }

    setIsDonationSubmitting(true);
    setDonationSubmitStatus('');

    try {
      // Prepare form data in application/x-www-form-urlencoded format
      const formData = new URLSearchParams();
      formData.append('fullName', donationForm.fullName);
      formData.append('email', donationForm.email);
      formData.append('donationAmount', donationAmount);
      formData.append('donationType', donationType);

      const response = await fetch(
        'https://readdy.ai/api/form/d3rvtghpsnv8430eot7g',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: formData.toString(),
        }
      );

      if (response.ok) {
        setDonationSubmitStatus(
          'Thank you for your generous donation! We will process your contribution and send you a confirmation.'
        );
        // Reset form
        setDonationForm({
          fullName: '',
          email: '',
        });
        setDonationAmount('50');
        setDonationType('one-time');
      } else {
        setDonationSubmitStatus(
          'There was an error processing your donation. Please try again.'
        );
      }
    } catch (error) {
      setDonationSubmitStatus(
        'There was an error processing your donation. Please try again.'
      );
    } finally {
      setIsDonationSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F3EE] to-white">
      {/* Header */}
      <header className="bg-[#1E3A47] text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <a href="/" className="cursor-pointer">
              <img
                src="https://static.readdy.ai/image/2406e7ca193108cdcc65309c2a1ba50d/cf3ff6764289504090a049c4e744ba2c.png"
                alt="Alleviate Care Foundation"
                className="h-12"
              />
            </a>
            <h1
              className="text-2xl font-normal"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              Foundation
            </h1>
          </div>
          <nav className="hidden md:flex space-x-8">
            <a
              href="#mission"
              className="hover:text-[#E9C46A] transition-colors cursor-pointer"
            >
              Mission
            </a>
            <a
              href="#volunteer"
              className="hover:text-[#E9C46A] transition-colors cursor-pointer"
            >
              Volunteer
            </a>
            <a
              href="#donate"
              className="hover:text-[#E9C46A] transition-colors cursor-pointer"
            >
              Donate
            </a>
            <a
              href="#education"
              className="hover:text-[#E9C46A] transition-colors cursor-pointer"
            >
              Education
            </a>
            <a
              href="#education"
              className="hover:text-[#E9C46A] transition-colors cursor-pointer"
              onClick={() =>
                document
                  .querySelector('a[href="tel:8005674433"]')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }
            >
              Contact
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-6 bg-gradient-to-r from-[#F5F3EE] to-white">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h1
              className="text-5xl lg:text-6xl font-bold text-[#1E3A47] leading-tight"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Extending Compassion Beyond Care
            </h1>
            <p
              className="text-xl text-[#5E6B70] leading-relaxed"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              The Alleviate Care Foundation is a nonprofit created by the team at
              Alleviate Care to expand our mission of compassion and dignity
              beyond the bedside. We bring comfort, education, and community
              connection to those facing serious illness —
              ensuring no one walks this path alone.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() =>
                  document
                    .getElementById('donate')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="bg-[#E9C46A] text-[#1E3A47] px-8 py-4 rounded-lg font-semibold hover:bg-[#d4b05a] transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Donate Now
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById('volunteer')
                    ?.scrollIntoView({ behavior: 'smooth' })
                }
                className="border-2 border-[#9DBA81] text-[#9DBA81] px-8 py-4 rounded-lg font-semibold hover:bg-[#9DBA81] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Volunteer With Us
              </button>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/fd50668befb0b37ac4c499904581e6e6.jpg"
              alt="Compassionate care moment"
              className="rounded-2xl shadow-2xl object-cover w-full h-[500px]"
            />
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section id="mission" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#1E3A47] mb-6"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Our Mission
            </h2>
            <p
              className="text-lg text-[#5E6B70] max-w-4xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              To extend the spirit of Alleviate Care — compassion,
              professionalism, and trust — into community programs that help
              families navigate the challenges of serious illness with clarity and
              confidence. We exist to educate, connect, and empower,
              making hospice and palliative support accessible and understood.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20worker%20standing%20in%20center%20leading%20educational%20group%20session%20with%20diverse%20group%20of%20older%20adults%20seated%20in%20comfortable%20circle%2C%20bright%20community%20center%20room%20with%20natural%20lighting%2C%20elderly%20participants%20actively%20listening%20and%20engaging%20in%20hospice%20care%20information%20session%2C%20warm%20welcoming%20atmosphere%20with%20educational%20materials%20visible%2C%20authentic%20community%20health%20education%20setting%20with%20caring%20professional%20facilitator&width=500&height=400&seq=mission5&orientation=landscape"
                alt="Community education session"
                className="rounded-xl shadow-lg object-cover w-full h-[400px]"
              />
            </div>
            <div className="space-y-6">
              <h3
                className="text-2xl font-semibold text-[#5E6B70]"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Our foundation supports families, caregivers, and volunteers
                through:
              </h3>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#9DBA81] rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span
                    className="text-[#5E6B70]"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Outreach programs that honor every individual's story with
                    empathy and respect
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#9DBA81] rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span
                    className="text-[#5E6B70]"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Community education that makes hospice care accessible and
                    understood
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-[#9DBA81] rounded-full flex items-center justify-center mt-1">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                  <span
                    className="text-[#5E6B70]"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Assistance funds that elevate the human experience during
                    end-of-life care
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Community Involvement */}
      <section className="py-20 px-6 bg-[#F5F3EE]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#1E3A47] mb-6"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Community Involvement
            </h2>
            <p
              className="text-lg text-[#5E6B70] max-w-3xl mx-auto"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              We collaborate with local senior centers, faith communities, and
              healthcare providers to bring compassionate support directly to
              those who need it most.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#E9C46A] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-presentation-line text-[#1E3A47] text-2xl"></i>
              </div>
              <h3
                className="text-xl font-semibold text-[#1E3A47] mb-3"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Education Sessions
              </h3>
              <p
                className="text-[#5E6B70]"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Hospice and advance-care planning education for families and
                communities
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#9DBA81] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-3-line text-white text-2xl"></i>
              </div>
              <h3
                className="text-xl font-semibold text-[#1E3A47] mb-3"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Support Programs
              </h3>
              <p
                className="text-[#5E6B70]"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Grief and caregiver support programs for families navigating
                difficult times
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#E9C46A] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-hand-heart-line text-[#1E3A47] text-2xl"></i>
              </div>
              <h3
                className="text-xl font-semibold text-[#1E3A47] mb-3"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Comfort Grants
              </h3>
              <p
                className="text-[#5E6B70]"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Financial assistance for families facing unexpected hospice-related
                expenses
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg text-center">
              <div className="w-16 h-16 bg-[#9DBA81] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-community-line text-white text-2xl"></i>
              </div>
              <h3
                className="text-xl font-semibold text-[#1E3A47] mb-3"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Remembrance Events
              </h3>
              <p
                className="text-[#5E6B70]"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Community gatherings celebrating life, legacy, and the bonds that
                connect us
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p
              className="text-lg text-[#1E3A47] font-medium italic"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              "Every initiative reflects our promise: compassionate,
              trustworthy, professional, approachable."
            </p>
          </div>
        </div>
      </section>

      {/* Volunteer Section */}
      <section id="volunteer" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#1E3A47] mb-6"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Volunteer With Us
            </h2>
            <p
              className="text-lg text-[#5E6B70] max-w-3xl mx-auto"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              Volunteers are the heartbeat of the Foundation. Whether offering
              companionship, supporting families, or helping at events, your
              presence brings light to those in need.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h3
                className="text-2xl font-semibold text-[#5E6B70] mb-6"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Volunteer Opportunities
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-[#F5F3EE] rounded-lg">
                  <div className="w-8 h-8 bg-[#E9C46A] rounded-full flex items-center justify-center">
                    <i className="ri-user-heart-line text-[#1E3A47]"></i>
                  </div>
                  <div>
                    <h4
                      className="font-semibold text-[#1E3A47]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Patient and Family Companionship
                    </h4>
                    <p
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Provide comfort and presence during visits
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F5F3EE] rounded-lg">
                  <div className="w-8 h-8 bg-[#9DBA81] rounded-full flex items-center justify-center">
                    <i className="ri-book-open-line text-white"></i>
                  </div>
                  <div>
                    <h4
                      className="font-semibold text-[#1E3A47]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Legacy Story Recording
                    </h4>
                    <p
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Help preserve precious memories and stories
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F5F3EE] rounded-lg">
                  <div className="w-8 h-8 bg-[#E9C46A] rounded-full flex items-center justify-center">
                    <i className="ri-calendar-event-line text-[#1E3A47]"></i>
                  </div>
                  <div>
                    <h4
                      className="font-semibold text-[#1E3A47]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Community Events
                    </h4>
                    <p
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Support outreach and educational programs
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-[#F5F3EE] rounded-lg">
                  <div className="w-8 h-8 bg-[#9DBA81] rounded-full flex items-center justify-center">
                    <i className="ri-team-line text-white"></i>
                  </div>
                  <div>
                    <h4
                      className="font-semibold text-[#1E3A47]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Administrative Support
                    </h4>
                    <p
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      Creative and organizational assistance
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right column – volunteer application form */}
            <div className="bg-[#F5F3EE] p-8 rounded-xl">
              <h3
                className="text-2xl font-semibold text-[#1E3A47] mb-6"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Join Our Volunteer Team
              </h3>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.includes('Thank you')
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus}
                </div>
              )}

              <form
                id="volunteer-form"
                data-readdy-form
                onSubmit={handleVolunteerSubmit}
                className="space-y-4"
              >
                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-2"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                    value={volunteerForm.name}
                    onChange={(e) =>
                      setVolunteerForm({
                        ...volunteerForm,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-2"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                    value={volunteerForm.email}
                    onChange={(e) =>
                      setVolunteerForm({
                        ...volunteerForm,
                        email: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-2"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                    value={volunteerForm.phone}
                    onChange={(e) =>
                      setVolunteerForm({
                        ...volunteerForm,
                        phone: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-2"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Areas of Interest
                  </label>
                  <div className="space-y-2">
                    {['Companionship', 'Legacy Stories', 'Events', 'Administrative'].map(
                      (interest) => (
                        <label
                          key={interest}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            name="interests"
                            value={interest}
                            className="rounded border-gray-300 text-[#9DBA81] focus:ring-[#9DBA81]"
                            checked={volunteerForm.interests.includes(interest)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setVolunteerForm({
                                  ...volunteerForm,
                                  interests: [...volunteerForm.interests, interest],
                                });
                              } else {
                                setVolunteerForm({
                                  ...volunteerForm,
                                  interests: volunteerForm.interests.filter(
                                    (i) => i !== interest
                                  ),
                                });
                              }
                            }}
                          />
                          <span
                            className="text-[#5E6B70]"
                            style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                          >
                            {interest}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-2"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Tell us about yourself
                  </label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent resize-none"
                    placeholder="Share your experience, availability, or any questions you have..."
                    value={volunteerForm.message}
                    onChange={(e) =>
                      setVolunteerForm({
                        ...volunteerForm,
                        message: e.target.value,
                      })
                    }
                  />
                  <p className="text-sm text-[#5E6B70] mt-1">
                    {volunteerForm.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isSubmitting
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-[#9DBA81] text-white hover:bg-[#8aa670]'
                  }`}
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Volunteer Application'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Donate Section */}
      <section id="donate" className="py-20 px-6 bg-[#F5F3EE]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#1E3A47] mb-6"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Make a Difference Today
            </h2>
            <p
              className="text-lg text-[#5E6B70] max-w-3xl mx-auto"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              Your gift makes a tangible difference. Each donation directly
              supports families with uncovered hospice expenses, caregiver
              education, and bereavement programs.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Beautiful%20hands%20holding%20glowing%20heart%20symbol%20with%20warm%20golden%20light%20rays%2C%20representing%20charitable%20giving%20and%20compassionate%20donation%20support%2C%20elegant%20modern%20composition%20in%20peaceful%20healthcare%20environment%20with%20soft%20natural%20background%2C%20professional%20donation%20concept%20with%20caring%20human%20touch%2C%20hope%20and%20generosity%20theme&width=500&height=400&seq=donate4&orientation=landscape"
                alt="Donation support"
                className="rounded-xl shadow-lg object-cover w-full h-[400px]"
              />
              <div className="mt-8 space-y-4">
                <h3
                  className="text-2xl font-semibold text-[#1E3A47]"
                  style={{ fontFamily: 'Source Serif Pro, serif' }}
                >
                  Your Impact
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#E9C46A] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-[#1E3A47] text-sm"></i>
                    </div>
                    <span
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      $25 provides comfort items for one family
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#E9C46A] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-[#1E3A47] text-sm"></i>
                    </div>
                    <span
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      $50 funds one grief support session
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-6 h-6 bg-[#E9C46A] rounded-full flex items-center justify-center">
                      <i className="ri-check-line text-[#1E3A47] text-sm"></i>
                    </div>
                    <span
                      className="text-[#5E6B70]"
                      style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                    >
                      $100 covers emergency hospice expenses
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3
                className="text-2xl font-semibold text-[#1E3A47] mb-6"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Secure Donation Form
              </h3>

              {donationSubmitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    donationSubmitStatus.includes('Thank you')
                      ? 'bg-green-100 text-green-800 border border-green-200'
                      : 'bg-red-100 text-red-800 border border-red-200'
                  }`}
                >
                  {donationSubmitStatus}
                </div>
              )}
              <form
                id="donation-form"
                data-readdy-form
                onSubmit={handleDonationSubmit}
                className="space-y-6"
              >
                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-3"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Donation Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="donationType"
                        value="one-time"
                        checked={donationType === 'one-time'}
                        onChange={(e) => setDonationType(e.target.value)}
                        className="text-[#9DBA81] focus:ring-[#9DBA81]"
                      />
                      <span
                        className="text-[#5E6B70]"
                        style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                      >
                        One-time Donation
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name="donationType"
                        value="monthly"
                        checked={donationType === 'monthly'}
                        onChange={(e) => setDonationType(e.target.value)}
                        className="text-[#9DBA81] focus:ring-[#9DBA81]"
                      />
                      <span
                        className="text-[#5E6B70]"
                        style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                      >
                        Monthly Donation
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-3"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Donation Amount ($)
                  </label>
                  <div className="grid grid-cols-4 gap-3 mb-4">
                    {['25', '50', '100', '250'].map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        className={`py-3 px-4 rounded-lg font-medium transition-colors ${
                          donationAmount === amount
                            ? 'bg-[#E9C46A] text-[#1E3A47]'
                            : 'bg-white text-[#5E6B70] border border-gray-300 hover:border-[#9DBA81]'
                        }`}
                        onClick={() => setDonationAmount(amount)}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#5E6B70]">
                      $
                    </span>
                    <input
                      type="number"
                      name="donationAmount"
                      min="1"
                      step="1"
                      required
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                      placeholder="Enter custom amount"
                      value={donationAmount}
                      onChange={(e) => setDonationAmount(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-3"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                    value={donationForm.fullName}
                    onChange={(e) =>
                      setDonationForm({
                        ...donationForm,
                        fullName: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label
                    className="block text-[#5E6B70] font-medium mb-3"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#9DBA81] focus:border-transparent"
                    value={donationForm.email}
                    onChange={(e) =>
                      setDonationForm({
                        ...donationForm,
                        email: e.target.value,
                      })
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={isDonationSubmitting}
                  className={`w-full py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    isDonationSubmitting
                      ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                      : 'bg-[#E9C46A] text-[#1E3A47] hover:bg-[#d4b05a]'
                  }`}
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  {isDonationSubmitting ? 'Processing...' : 'Complete Donation'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2
              className="text-4xl font-semibold text-[#1E3A47] mb-6"
              style={{ fontFamily: 'Source Serif Pro, serif' }}
            >
              Education &amp; Resources
            </h2>
            <p
              className="text-lg text-[#5E6B70] max-w-3xl mx-auto"
              style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
            >
              Knowledge empowers families to make informed decisions about
              end-of-life care. Explore our educational resources, blog posts,
              and comprehensive guides to hospice care.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Friendly%20community%20volunteer%20sitting%20at%20table%20with%20small%20group%20of%20elderly%20people%20sharing%20educational%20brochures%20and%20hospice%20care%20information%2C%20warm%20intimate%20setting%20in%20community%20center%2C%20volunteer%20pointing%20to%20helpful%20resources%20while%20elderly%20participants%20listen%20attentively%2C%20natural%20lighting%20creating%20comfortable%20learning%20environment%2C%20authentic%20community%20outreach%20scene%20with%20caring%20volunteer%20providing%20guidance%20and%20support%20to%20seniors&width=500&height=400&seq=education7&orientation=landscape"
                alt="Community volunteer sharing resources"
                className="rounded-xl shadow-lg object-cover w-full h-[400px]"
              />
            </div>

            <div className="space-y-8">
              <div>
                <h3
                  className="text-2xl font-semibold text-[#1E3A47] mb-6"
                  style={{ fontFamily: 'Source Serif Pro, serif' }}
                >
                  Educational Resources
                </h3>
                <div className="space-y-4">
                  <a
                    href="https://alleviate.care/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-lg hover:bg-[#E9C46A] transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#9DBA81] rounded-full flex items-center justify-center group-hover:bg-[#1E3A47] transition-colors">
                        <i className="ri-article-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4
                          className="font-semibold text-[#1E3A47] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Alleviate Care Blog
                        </h4>
                        <p
                          className="text-[#5E6B70] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Latest insights on hospice care and family support
                        </p>
                      </div>
                    </div>
                    <i className="ri-external-link-line text-[#5E6B70] group-hover:text-[#1E3A47]"></i>
                  </a>

                  <a
                    href="https://alleviate.care/resources"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-lg hover:bg-[#E9C46A] transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#E9C46A] rounded-full flex items-center justify-center group-hover:bg-[#1E3A47] transition-colors">
                        <i className="ri-book-open-line text-[#1E3A47] group-hover:text-white text-xl"></i>
                      </div>
                      <div>
                        <h4
                          className="font-semibold text-[#1E3A47] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Care Guides &amp; Resources
                        </h4>
                        <p
                          className="text-[#5E6B70] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Comprehensive guides for families and caregivers
                        </p>
                      </div>
                    </div>
                    <i className="ri-external-link-line text-[#5E6B70] group-hover:text-[#1E3A47]"></i>
                  </a>

                  <a
                    href="https://alleviate.care/about"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-lg hover:bg-[#E9C46A] transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#9DBA81] rounded-full flex items-center justify-center group-hover:bg-[#1E3A47] transition-colors">
                        <i className="ri-information-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4
                          className="font-semibold text-[#1E3A47] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          About Alleviate Care
                        </h4>
                        <p
                          className="text-[#5E6B70] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Learn about our mission and approach to care
                        </p>
                      </div>
                    </div>
                    <i className="ri-external-link-line text-[#5E6B70] group-hover:text-[#1E3A47]"></i>
                  </a>

                  <a
                    href="https://alleviate.care/services"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-4 bg-[#F5F3EE] rounded-lg hover:bg-[#E9C46A] transition-colors group cursor-pointer"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-[#E9C46A] rounded-full flex items-center justify-center group-hover:bg-[#1E3A47] transition-colors">
                        <i className="ri-service-line text-[#1E3A47] group-hover:text-white text-xl"></i>
                      </div>
                      <div>
                        <h4
                          className="font-semibold text-[#1E3A47] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Our Services
                        </h4>
                        <p
                          className="text-[#5E6B70] group-hover:text-[#1E3A47]"
                          style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                        >
                          Learn about our comprehensive care services
                        </p>
                      </div>
                    </div>
                    <i className="ri-external-link-line text-[#5E6B70] group-hover:text-[#1E3A47]"></i>
                  </a>
                </div>
              </div>

              <div className="bg-[#F5F3EE] p-6 rounded-xl">
                <h4
                  className="text-xl font-semibold text-[#1E3A47] mb-4"
                  style={{ fontFamily: 'Source Serif Pro, serif' }}
                >
                  Need Help Getting Started?
                </h4>
                <p
                  className="text-[#5E6B70] mb-4"
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  Our team is here to guide you through every step.
                </p>
                <a
                  href="tel:8005674433"
                  className="inline-block bg-[#1E3A47] text-white px-6 py-4 rounded-lg hover:bg-[#16313e]"
                >
                  Call Us: (800) 567-4433
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1E3A47] text-white py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Foundation Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <a href="/" className="cursor-pointer">
                  <img
                    src="https://static.readdy.ai/image/2406e7ca193108cdcc65309c2a1ba50d/cf3ff6764289504090a049c4e744ba2c.png"
                    alt="Alleviate Care Foundation"
                    className="h-12"
                  />
                </a>
                <h3
                  className="text-2xl font-normal"
                  style={{ fontFamily: 'Source Serif Pro, serif' }}
                >
                  Foundation
                </h3>
              </div>
              <p
                className="text-gray-300 mb-6 leading-relaxed"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                Extending compassion beyond care through community outreach,
                education, and support programs that empower families navigating
                serious illness.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.facebook.com/AlleviateCare-61568793485867/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#E9C46A] rounded-full flex items-center justify-center hover:bg-[#d4b05a] transition-colors"
                >
                  <i className="ri-facebook-fill text-[#1E3A47]"></i>
                </a>
                <a
                  href="https://www.instagram.com/alleviate.care/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#E9C46A] rounded-full flex items-center justify-center hover:bg-[#d4b05a] transition-colors"
                >
                  <i className="ri-instagram-line text-[#1E3A47]"></i>
                </a>
                <a
                  href="https://www.linkedin.com/company/alleviate-care-us"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#E9C46A] rounded-full flex items-center justify-center hover:bg-[#d4b05a] transition-colors"
                >
                  <i className="ri-linkedin-line text-[#1E3A47]"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4
                className="text-lg font-semibold mb-4"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Quick Links
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href="#mission"
                    className="text-gray-300 hover:text-[#E9C46A] transition-colors"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Our Mission
                  </a>
                </li>
                <li>
                  <a
                    href="#volunteer"
                    className="text-gray-300 hover:text-[#E9C46A] transition-colors"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Volunteer
                  </a>
                </li>
                <li>
                  <a
                    href="#donate"
                    className="text-gray-300 hover:text-[#E9C46A] transition-colors"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Donate
                  </a>
                </li>
                <li>
                  <a
                    href="#education"
                    className="text-gray-300 hover:text-[#E9C46A] transition-colors"
                    style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                  >
                    Resources
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4
                className="text-lg font-semibold mb-4"
                style={{ fontFamily: 'Source Serif Pro, serif' }}
              >
                Contact
              </h4>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <i className="ri-mail-line mt-1"></i>
                  <span style={{ fontFamily: 'Source Sans Pro, sans-serif' }}>
                    info@alleviatecare.com
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-phone-line mt-1"></i>
                  <span style={{ fontFamily: 'Source Sans Pro, sans-serif' }}>
                    (800) 567-4433
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-map-pin-line mt-1"></i>
                  <span style={{ fontFamily: 'Source Sans Pro, sans-serif' }}>
                    Serving communities across southern California
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p
                className="text-gray-400 text-sm"
                style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
              >
                © 2024 Alleviate Care Foundation. All rights reserved.
              </p>
              <div className="flex space-x-6 mt-5 md:mt-0">
                <a
                  href="#"
                  className="text-gray-400 text-sm hover:text-[#E9C46A] transition-colors"
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  Privacy Policy
                </a>
                <a
                  href="#"
                  className="text-gray-400 text-sm hover:text-[#E9C46A] transition-colors"
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  Terms of Service
                </a>
                <a
                  href="https://readdy.ai/?origin=logo"
                  className="text-gray-400 text-sm hover:text-[#E9C46A] transition-colors"
                  style={{ fontFamily: 'Source Sans Pro, sans-serif' }}
                >
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
